import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, LogIn, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useDevAuth } from "@/context/DevAuthContext";

const loginUrl = (email) => `/espace-developpeur/connexion?type=minisite${email ? `&email=${encodeURIComponent(email)}` : ""}`;

// Unsigned link for another account: never show that person's businesses, offer to switch.
const WrongAccount = ({ email, after }) => {
  const { t } = useI18n();
  const { logout } = useDevAuth();
  const navigate = useNavigate();
  const sw = async () => {
    sessionStorage.setItem("fmo_after_login", after);
    await logout();
    navigate(loginUrl(email));
  };
  return (
    <div className="mx-auto max-w-xl px-4 py-16" data-testid="client-link-wrong-account">
      <div className="card-carbon min-w-0 space-y-4 rounded-2xl p-5">
        <h1 className="flex items-start gap-2 break-words font-display text-base font-bold text-white"><ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
          <span className="min-w-0 break-all">{t("link.wrongTitle", { email })}</span></h1>
        <p className="break-words text-sm text-neutral-300">{t("link.wrongText", { email })}</p>
        <div className="flex flex-wrap gap-3">
          <button type="button" className="btn-gold btn-sm btn-cta max-w-full whitespace-normal" onClick={sw} data-testid="client-link-switch">
            <LogIn className="h-4 w-4 shrink-0" /> <span className="min-w-0 break-all">{t("link.switch", { email })}</span></button>
          <Link to="/espace-developpeur" className="btn-red btn-sm btn-cta" data-testid="client-link-stay">{t("nav.mySpace")}</Link>
        </div>
      </div>
    </div>
  );
};

// Signed link (&sig= from the mini-site): link the REF(s) to the logged-in owner, whatever email they were registered with.
const useSignedLink = (dev, params) => {
  const sig = params.get("sig");
  const [state, setState] = useState(sig ? "pending" : "none");
  useEffect(() => {
    if (!dev || !sig) return;
    const refs = (params.get("refs") || params.get("ref") || "").split(",").map((r) => r.trim()).filter(Boolean);
    api.post("/dev/billing/cart/link", { app: params.get("app") || "", refs, email: params.get("email") || "", sig })
      .then(() => setState("ok")).catch(() => setState("bad"));
  }, [dev, sig]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
};

// /espace-client?app=<app_id>&email=<email>&ref=<REF>|refs=<R1,R2>[&panier=1][&sig=…]: the logged-in account decides.
export default function ClientSpace() {
  const { dev } = useDevAuth();
  const [params] = useSearchParams();
  const { search } = useLocation();
  const signed = useSignedLink(dev, params);
  const hint = (params.get("email") || "").trim().toLowerCase();
  const here = `/espace-client${search}`;
  const wait = <div className="flex justify-center py-24" data-testid="client-link-loading"><Loader2 className="h-6 w-6 animate-spin text-[var(--gold)]" /></div>;
  if (dev === null) return wait;
  if (!dev) {
    if (search) sessionStorage.setItem("fmo_after_login", here);
    return <Navigate to={loginUrl(hint)} replace />;
  }
  if (signed === "pending") return wait;
  if (signed !== "ok" && hint && (dev.email || "").toLowerCase() !== hint) return <WrongAccount email={hint} after={here} />;
  const q = new URLSearchParams(search);
  ["email", "sig"].forEach((k) => q.delete(k));
  if (q.get("refs")) { q.set("ref", q.get("refs")); q.delete("refs"); }
  return <Navigate to={`/espace-developpeur${q.toString() ? `?${q}` : ""}${q.get("panier") || q.get("ref") ? "#panier" : ""}`} replace />;
}
